import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { ArrowLeft, Check, Clock, AlertCircle, ExternalLink } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { toast } from 'sonner';

const STEPS = [
  { id: 1, name: 'Resumen', description: 'Confirmar pago' },
  { id: 2, name: 'Dirección', description: 'Obtener wallet' },
  { id: 3, name: 'Envío', description: 'Realizar pago' },
  { id: 4, name: 'Confirmación', description: 'Detectando fondeo' },
  { id: 5, name: 'Completado', description: 'Pago exitoso' },
];

export default function PaymentPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState(null);
  const [copying, setCopying] = useState(false);

  // Fetch order data
  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  // Poll for order status
  useEffect(() => {
    if (!order) return;

    const interval = setInterval(() => {
      fetchOrder();
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(interval);
  }, [order]);

  // Update countdown
  useEffect(() => {
    if (!order || !order.expires_at) return;

    const interval = setInterval(() => {
      const now = new Date();
      const expiresAt = new Date(order.expires_at);
      const diff = expiresAt - now;

      if (diff <= 0) {
        setTimeLeft('Expirado');
        clearInterval(interval);
      } else {
        const minutes = Math.floor(diff / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        setTimeLeft(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [order]);

  // Update step based on status
  useEffect(() => {
    if (!order) return;

    if (order.status === 'PENDING') {
      if (currentStep < 2) setCurrentStep(2);
    } else if (order.status === 'FUNDED_DETECTED') {
      setCurrentStep(4);
    } else if (order.status === 'FORWARDED') {
      setCurrentStep(5);
    } else if (order.status === 'EXPIRED') {
      toast.error('La orden ha expirado');
    }
  }, [order]);

  const fetchOrder = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/orders/${orderId}`);

      if (!response.ok) {
        throw new Error('Orden no encontrada');
      }

      const data = await response.json();
      setOrder(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching order:', error);
      toast.error('Error al cargar la orden');
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopying(true);
      toast.success(`${label} copiado al portapapeles`);
      setTimeout(() => setCopying(false), 2000);
    } catch (err) {
      toast.error('Error al copiar');
    }
  };

  const handleContinue = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[hsl(var(--primary))] mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Cargando orden...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="max-w-md w-full p-8 text-center">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Orden no encontrada</h1>
          <p className="text-muted-foreground mb-6">
            La orden solicitada no existe o ha sido eliminada.
          </p>
          <Button onClick={() => navigate('/')} variant="default">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver al inicio
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[hsl(190_60%_95%)] to-[hsl(205_60%_96%)] py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <h1 className="text-2xl font-bold">FluxiPay</h1>
          {timeLeft && order.status === 'PENDING' && (
            <Badge variant="outline" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              {timeLeft}
            </Badge>
          )}
        </div>

        {/* Stepper */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center flex-1">
                <div className="flex flex-col items-center relative">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${
                      currentStep >= step.id
                        ? 'bg-[hsl(var(--primary))] border-[hsl(var(--primary))] text-white'
                        : 'bg-white border-gray-300 text-gray-400'
                    }`}
                  >
                    {currentStep > step.id ? (
                      <Check className="h-5 w-5" />
                    ) : (
                      <span className="text-sm font-semibold">{step.id}</span>
                    )}
                  </div>
                  <span className="text-xs mt-2 text-center hidden sm:block">
                    {step.name}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 transition-colors ${
                      currentStep > step.id ? 'bg-[hsl(var(--primary))]' : 'bg-gray-300'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <Card className="p-6 sm:p-8">
          {/* Step 1: Summary */}
          {currentStep === 1 && (
            <div data-testid="step-summary">
              <h2 className="text-xl sm:text-2xl font-bold mb-4">Resumen del pago</h2>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Monto:</span>
                  <span className="font-semibold">
                    {order.amount_crypto} {order.crypto_asset.replace('-', ' ')}
                  </span>
                </div>
                {order.amount_fiat && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Equivalente:</span>
                    <span>≈ ${order.amount_fiat} {order.currency_fiat}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Red:</span>
                  <Badge variant="secondary">{order.network}</Badge>
                </div>
                <Separator />
                <p className="text-sm text-muted-foreground">
                  Esta orden expira en {timeLeft}. Después de ese tiempo, deberás crear una nueva orden.
                </p>
              </div>
              <Button
                data-testid="btn-continue-step1"
                onClick={handleContinue}
                className="w-full mt-6 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--brand-teal-600))]"
              >
                Continuar
              </Button>
            </div>
          )}

          {/* Step 2 & 3: Address + QR */}
          {(currentStep === 2 || currentStep === 3) && (
            <div data-testid="step-address-qr">
              <h2 className="text-xl sm:text-2xl font-bold mb-4">
                Envía {order.amount_crypto} {order.crypto_asset.replace('-', ' ')}
              </h2>

              {/* QR Code */}
              <div className="flex justify-center my-6">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <QRCodeCanvas
                    value={order.deposit_address}
                    size={200}
                    level="H"
                    includeMargin={true}
                  />
                </div>
              </div>

              {/* Address */}
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-muted-foreground">Dirección de depósito:</label>
                  <div className="mt-2 flex items-center gap-2">
                    <code className="flex-1 p-3 bg-muted rounded text-sm break-all">
                      {order.deposit_address}
                    </code>
                    <Button
                      data-testid="btn-copy-address"
                      onClick={() => copyToClipboard(order.deposit_address, 'Dirección')}
                      variant="outline"
                      size="sm"
                    >
                      {copying ? 'Copiado!' : 'Copiar'}
                    </Button>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-muted-foreground">Monto exacto:</label>
                  <div className="mt-2 flex items-center gap-2">
                    <code className="flex-1 p-3 bg-muted rounded text-sm font-semibold">
                      {order.amount_crypto} {order.crypto_asset.replace('-', ' ')}
                    </code>
                    <Button
                      onClick={() => copyToClipboard(order.amount_crypto.toString(), 'Monto')}
                      variant="outline"
                      size="sm"
                    >
                      Copiar
                    </Button>
                  </div>
                </div>

                <div className="bg-[hsl(var(--accent))] p-4 rounded-lg">
                  <p className="text-sm">
                    <strong>⚠️ Importante:</strong>
                  </p>
                  <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                    <li>• Envía exactamente {order.amount_crypto} {order.crypto_asset.replace('-', ' ')}</li>
                    <li>• Red: {order.network}</li>
                    <li>• Esta dirección es temporal y única para tu orden</li>
                    <li>• Los fondos se detectarán automáticamente</li>
                  </ul>
                </div>
              </div>

              {currentStep === 2 && (
                <Button
                  onClick={handleContinue}
                  className="w-full mt-6 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--brand-teal-600))]"
                >
                  Ya envié el pago
                </Button>
              )}
            </div>
          )}

          {/* Step 4: Detecting */}
          {currentStep === 4 && (
            <div data-testid="step-detecting" className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[hsl(var(--primary))] mx-auto mb-6"></div>
              <h2 className="text-xl sm:text-2xl font-bold mb-4">
                {order.status === 'FUNDED_DETECTED'
                  ? 'Pago detectado'
                  : 'Detectando tu pago...'}
              </h2>
              <p className="text-muted-foreground">
                {order.status === 'FUNDED_DETECTED'
                  ? 'Procesando transferencia a wallet concentradora...'
                  : 'Esperando confirmación en la blockchain. Esto puede tomar unos minutos.'}
              </p>
            </div>
          )}

          {/* Step 5: Completed */}
          {currentStep === 5 && (
            <div data-testid="step-completed" className="text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold mb-4">¡Pago completado!</h2>
              <p className="text-muted-foreground mb-6">
                Tu pago ha sido procesado exitosamente y los fondos han sido reenviados.
              </p>

              <div className="bg-[hsl(var(--accent))] p-4 rounded-lg mb-6 text-left">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Orden ID:</span>
                    <span className="font-mono text-xs">{order.id.slice(0, 8)}...</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Monto:</span>
                    <span className="font-semibold">
                      {order.amount_crypto} {order.crypto_asset.replace('-', ' ')}
                    </span>
                  </div>
                  {order.forwarding_tx_hash && (
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">TX Hash:</span>
                      <a
                        href={`https://nile.tronscan.org/#/transaction/${order.forwarding_tx_hash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[hsl(var(--primary))] hover:underline flex items-center gap-1 text-xs"
                      >
                        Ver en explorer
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  )}
                </div>
              </div>

              <Button
                onClick={() => navigate('/')}
                className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--brand-teal-600))]"
              >
                Volver al inicio
              </Button>
            </div>
          )}
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground">
            ¿Necesitas ayuda?{' '}
            <a href="mailto:hola@fluxipay.com" className="text-[hsl(var(--primary))] hover:underline">
              Contáctanos
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
